import { useEffect, useState, type FormEvent } from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import { useWorklistSelection } from './pathology.hooks';
import { useAdvancePathologyStatus, usePathologyWorklist } from './pathology.queries';
import { defaultStatusForStage, pathologyStageConfig, pathologyStages, pathologyStageStatusOptions, todayInput } from './pathology.utils';
import type { PathologyListFilters, PathologyStage } from './types';
import { WorklistFilterBar } from './WorklistFilterBar';
import { WorklistPagination } from './WorklistPagination';
import { WorklistTable } from './WorklistTable';

const FILTERS_STORAGE_KEY = 'pathology-worklist-filters';

const initialFilters = (stage: PathologyStage): PathologyListFilters => ({ page: 1, limit: 20, stage, status: defaultStatusForStage[stage], fromDate: todayInput(), toDate: todayInput() });

interface StoredWorklist {
  stage: PathologyStage;
  draft: PathologyListFilters;
  filters: PathologyListFilters;
}

const readStoredWorklist = (): StoredWorklist | null => {
  try {
    const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredWorklist> | null;
    if (!parsed || typeof parsed !== 'object' || !parsed.draft || !parsed.filters) return null;
    const stage = pathologyStages.includes(parsed.stage as PathologyStage) ? (parsed.stage as PathologyStage) : null;
    if (!stage) return null;
const sanitize = (value: Partial<PathologyListFilters>): PathologyListFilters => ({
      stage,
      page: typeof value.page === 'number' && value.page > 0 ? value.page : 1,
      limit: typeof value.limit === 'number' && value.limit > 0 ? value.limit : 20,
      ...(value.field ? { field: value.field } : {}),
      ...(typeof value.value === 'string' ? { value: value.value } : {}),
      status: defaultStatusForStage[stage],
      ...(typeof value.chargeIds === 'string' ? { chargeIds: value.chargeIds } : {}),
      fromDate: typeof value.fromDate === 'string' ? value.fromDate : todayInput(),
      toDate: typeof value.toDate === 'string' ? value.toDate : todayInput(),
      ...(value.sortBy ? { sortBy: value.sortBy, sortOrder: value.sortOrder === 'desc' ? 'desc' as const : 'asc' as const } : {}),
    });
    return { stage, draft: sanitize(parsed.draft), filters: sanitize(parsed.filters) };
  } catch {
    return null;
  }
};

export const PathologyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [restored] = useState(() => (navigationType === NavigationType.Pop ? readStoredWorklist() : null));
  const defaultStage: PathologyStage = restored?.stage ?? 'collection';
  const [stage, setStage] = useState<PathologyStage>(defaultStage);
  const [draft, setDraft] = useState<PathologyListFilters>(() => restored?.draft ?? initialFilters(defaultStage));
  const [filters, setFilters] = useState<PathologyListFilters>(() => restored?.filters ?? initialFilters(defaultStage));
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  const selection = useWorklistSelection();
  const reportSelection = useWorklistSelection();
  const { showToast } = useToast();

  const query = usePathologyWorklist(filters);
  const advance = useAdvancePathologyStatus();
  const busy = query.isFetching;
  const stageConfig = pathologyStageConfig[stage];
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  // Switching tabs keeps the applied filters and re-queries the new stage on them.
  const changeStage = (next: PathologyStage) => {
    setStage(next);
    setDraft((current) => ({ ...current, stage: next, status: defaultStatusForStage[next], page: 1 }));
    setFilters((current) => ({ ...current, stage: next, status: defaultStatusForStage[next], page: 1 }));
    setExpandedBillId(null);
    selection.clear();
    reportSelection.clear();
  };

  useEffect(() => {
    window.sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ stage, draft, filters }));
  }, [stage, draft, filters]);

  // A fresh entry through a sidebar/menu link always starts from the default filters.
  useEffect(() => {
    if (navigationType === NavigationType.Pop) return;
    const next = initialFilters('collection');
    setStage('collection');
    setDraft(next);
    setFilters(next);
    setExpandedBillId(null);
    selection.clear();
    reportSelection.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navigationType]);

  useEffect(() => {
    const totalPages = query.data?.pagination.totalPages;
    if (totalPages && filters.page > totalPages) setFilters((current) => ({ ...current, page: totalPages }));
  }, [filters.page, query.data?.pagination.totalPages]);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft, page: 1 });
  };
  const reset = () => { const next = initialFilters(stage); setDraft(next); setFilters(next); };
  const today = () => { const date = todayInput(); setDraft((current) => ({ ...current, fromDate: date, toDate: date })); setFilters((current) => ({ ...current, fromDate: date, toDate: date, page: 1 })); };

  const toggleExpand = (billId: string) => {
    setExpandedBillId((current) => (current === billId ? null : billId));
    selection.clear();
    reportSelection.clear();
  };

  const openReportUpload = (billId: string, lineIds: string[]) => {
    if (lineIds.length === 0) return;
    void navigate(`/pathology/report-upload/${billId}?lineIds=${lineIds.join(',')}`);
  };

  const advanceLines = (billId: string, lineIds: string[]) => {
    if (lineIds.length === 0) return;
    void confirmUpdate(`the status of ${lineIds.length} selected test${lineIds.length === 1 ? '' : 's'}`).then((confirmed) => {
      if (!confirmed) return;
      advance.mutate({ billId, lineIds, action: stageConfig.action }, {
        onSuccess: () => { selection.removeMany(billId, lineIds); showToast('Status updated successfully.', 'success'); },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The status update could not be saved.', 'error'),
      });
    });
  };

  const rollbackLines = (billId: string, lineIds: string[]) => {
    if (lineIds.length === 0) return;
    void confirmUpdate(`rollback for ${lineIds.length} selected test${lineIds.length === 1 ? '' : 's'}`).then((confirmed) => {
      if (!confirmed) return;
      advance.mutate({ billId, lineIds, action: 'rollback' }, {
        onSuccess: () => {
          selection.removeMany(billId, lineIds);
          reportSelection.removeMany(billId, lineIds);
          showToast('Report rolled back successfully.', 'success');
        },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The rollback could not be saved.', 'error'),
      });
    });
  };

  return (
    <div className="page-stack pathology-page">
      <div className="page-heading"><div><p className="eyebrow">Investigation</p><h1>Pathology</h1><p>Track pathology samples from collection through report generation.</p></div></div>

      <div className="pathology-tabs" role="tablist">
        {pathologyStages.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={stage === item}
            className={`pathology-tab${stage === item ? ' active' : ''}`}
            onClick={() => changeStage(item)}
          >
            {pathologyStageConfig[item].label}
          </button>
        ))}
      </div>

      <section className="content-card investigation-bill-card pathology-card">
        <WorklistFilterBar draft={draft} onDraftChange={setDraft} onSubmit={apply} onToday={today} onReset={reset} busy={busy} statusOptions={pathologyStageStatusOptions[stage]} />

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load pathology worklist" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? <EmptyState title={`No bills in ${stageConfig.label}`} message="Change the filters or check back once tests reach this stage." /> : null}

        {query.isSuccess && query.data.data.length > 0 ? <>
          <WorklistTable
            bills={query.data.data}
            tableSort={tableSort}
            expandedBillId={expandedBillId}
            onToggleExpand={toggleExpand}
            isSelected={selection.isSelected}
            onToggleLine={selection.toggleLine}
            onToggleAllInBill={selection.toggleAllInBill}
            selectedInBill={selection.selectedInBill}
            actionLabel={stageConfig.actionLabel}
            action={stageConfig.action}
            actionPending={advance.isPending}
            onAdvance={advanceLines}
            showReportUpdate={stage === 'report-generated'}
            reportSelection={{
              isSelected: reportSelection.isSelected,
              onToggleLine: reportSelection.toggleLine,
              onToggleAllInBill: reportSelection.toggleAllInBill,
              selectedInBill: reportSelection.selectedInBill,
            }}
            onOpenReportUpload={openReportUpload}
            {...(stage === 'collection' ? {} : { onRollback: rollbackLines })}
          />
          <WorklistPagination
            page={filters.page}
            limit={filters.limit}
            totalPages={query.data.pagination.totalPages}
            busy={busy}
            onLimitChange={(limit) => { setDraft((current) => ({ ...current, limit })); setFilters((current) => ({ ...current, limit, page: 1 })); }}
            onPrev={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}
            onNext={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          />
        </> : null}
      </section>
    </div>
  );
};
